import { ChannelSound, resolveSound } from "./notificationSounds";

// Debounce tracking — prevents overlapping playback from burst arrivals.
const DEBOUNCE_MS = 500;
const lastPlayedById: Record<string, number> = {};

// One cached Audio element per source, so we don't reallocate on every play.
const audioCache = new Map<string, HTMLAudioElement>();
const getAudio = (src: string): HTMLAudioElement => {
  let audio = audioCache.get(src);
  if (!audio) {
    audio = new Audio(src);
    audioCache.set(src, audio);
  }
  return audio;
};

const clampVolume = (volume: number) => Math.min(1, Math.max(0, volume));

/**
 * Plays a channel's chosen notification sound at its volume. Unified path for
 * every notification channel (headlines / truth social / portfolio). Debounced
 * per sound id (500ms) so a burst of arrivals doesn't stack overlapping audio.
 */
export const playNotificationSound = ({ soundId, volume }: ChannelSound) => {
  const sound = resolveSound(soundId);

  const now = Date.now();
  if (now - (lastPlayedById[sound.id] ?? 0) < DEBOUNCE_MS) return;
  lastPlayedById[sound.id] = now;

  const audio = getAudio(sound.src);
  audio.volume = clampVolume(volume);
  audio.play().catch((error) => {
    console.error("Error playing sound:", error);
  });
};

// Thin convenience wrappers retained for the dev TestComponent / legacy callers.
export const playChime = (volume = 0.5) =>
  playNotificationSound({ soundId: "chime", volume });

export const playSpoon = (volume = 0.5) =>
  playNotificationSound({ soundId: "spoon", volume });

export const playRisingPopSound = (volume = 0.5) =>
  playNotificationSound({ soundId: "rising-pops", volume });
